<template>
  <v-navigation-drawer
    v-model="isMainSidebarOpen"
    ref="sidebarDrawer"
    clipped
    bottom
    app
    width="325px"
    mobile-breakpoint="769"
    class="sidebar"
  >
    <v-list
      nav
      class="sidebar__nav"
    >
      <v-list-item-group active-class="sidebar__item--active">
        <template v-for="(item, i) in nonAccountLinks">
          <v-list-item
            v-if="!item.isSuperOnly || isSuperAdmin"
            :id="item.title.replace(/[^a-zA-Z0-9]/g, '')"
            :key="`nonaccount-link-${i}`"
            :to="item.url"
            class="sidebar__item text-decoration-none"
            :class="item.cssClass"
            link
            exact
            :dense="item.type === 'account'"
          >
            <v-list-item-content>
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <v-divider
          key="divider"
          class="my-3"
        />
        <div class="d-flex">
          <v-spacer />
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                small
                v-bind="attrs"
                v-on="on"
                color="primary"
              >
                {{ selectedStatus }} Acounts <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item-group>
                <v-list-item
                  v-for="(status, i) in accountStatuses"
                  :key="i"
                  @click="filterAccountsByStatus(status)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ status }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
        <template v-for="(item, i) in accountLinks">
          <v-tooltip
            open-delay="1500"
            right
            :key="`account-link-${i}`"
          >
            <template v-slot:activator="{ on}">
              <v-list-item
                :to="item.url"
                :id="item.title.replace(/[^a-zA-Z0-9]/g, '')"
                class="sidebar__item text-decoration-none"
                :class="item.cssClass"
                link
                exact
                v-on="on"
                :dense="item.type === 'account'"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>
        </template>
      </v-list-item-group>
    </v-list>
    <template v-if="isSuperAdmin">
      <v-btn
        absolute
        bottom
        right
        class="scroll-top-fab"
        color="primary"
        fab
        small
        @click="scrollToTop"
      >
        <v-icon>mdi-chevron-up</v-icon>
      </v-btn>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from "vuex";
import { MOBILE_MAX_WIDTH } from "@/constants";

export default {
  props: {
  },
  data() {
    return {
      nonAccountLinks: [
        {
          title: "Dashboard",
          url: "/v/",
          type: "nonAccount"
        },
        {
          title: "Super Admin Dashboard",
          url: "/v/super/home",
          type: "nonAccount",
          isSuperOnly: true,
        }
      ],
      isMainSidebarOpen: false,
      selectedStatus: 'Active',
      accountStatuses: [
        'Active',
        'Paused',
        'Application',
        'Archived'
      ],
    };
  },
  computed: {
    ...mapGetters({
      mainSidebarOpenState: "app/getMainSidebarOpenState",
      accounts: "accounts/getAccounts",
      isSuperAdmin: "auth/isSuperAdmin",
    }),
    filteredAccounts() {
      const statusMap = {
        'Application': 'Apply',
        'Paused': 'Locked',
        'Archived': 'Deleted',
        'Active': 'Open',
      };
      const targetStatus = statusMap[this.selectedStatus] || 'Open';
      return this.accounts.filter(account => account.accountStatus === targetStatus);
    },
    accountLinks() {
      return this.filteredAccounts.map((account) => {
        const url = `/v/account/${account.accountId}`;
        return {
          title: account.accountName,
          url: url,
          type: "account",
          cssClass: this.getLinkCssClass(url),
          accountStatus: account.accountStatus,
        }
      });
    },
  },
  watch: {
    mainSidebarOpenState(newValue) {
      this.isMainSidebarOpen = newValue;
    },
    isMainSidebarOpen(isOpen) {
      this.$store.dispatch("app/setMainSidebarOpenState", isOpen);
    },
    $route(newValue, oldValue) {
      // Hide main sidebar when going from non-account page to account page
      if (!oldValue.path.includes('/account/') && newValue.path.includes('/account/')) {
        this.isMainSidebarOpen = false;
      }

      // Show main sidebar when going from account page to non-account page
      if (oldValue.path.includes('/account/') && !newValue.path.includes('/account/')) {
        this.isMainSidebarOpen = true;
      }
    },
  },
  mounted() {
    let breakpoint = this.$vuetify.breakpoint;
    if (breakpoint.width <= MOBILE_MAX_WIDTH) {
      this.isMainSidebarOpen = false;
    } else {
      this.isMainSidebarOpen = this.$store.getters["app/getMainSidebarOpenState"];
    }
  },
  methods: {
    isCurrentUrlASubpath(currentPath, parentPath) {
      return currentPath.includes(parentPath);
    },
    getLinkCssClass(url) {
      return this.$route.path && this.isCurrentUrlASubpath(this.$route.path, url) ?
        'sidebar__item--active v-item--active v-list-item--active' :
        '';
    },
    filterAccountsByStatus(status) {
      this.selectedStatus = status;
    },
    scrollToTop() {
      this.$nextTick(() => {
        const drawerContent = this.$refs.sidebarDrawer?.$el?.querySelector('.v-navigation-drawer__content');
        if (drawerContent) {
          drawerContent.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.sidebar {
  padding: 25px 10px;
  letter-spacing: 1.7px;
  &__title {
    font-size: 10pt;
    text-transform: uppercase;
    color: #8dabc4 !important;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 10px 25px;
  }
  &__nav {
    padding: 0 16px 0 !important;
    &--item {
      margin: 8px 0;
      padding: 0 !important;
      .v-list-item__content {
        padding: 10px 25px !important;
      }
      &.account {
        .v-list-item__title {
          font-size: 14px;
          font-weight: 400;
          overflow: auto;
          text-overflow: unset;
          white-space: normal;
        }
      }
    }
  }
}

.sidebar .scroll-top-fab .theme--light.v-icon {
  color: white;
}

.theme--light.v-list-item--active.sidebar__item--active:before,
.theme--light.v-list-item--active.sidebar__item--active:hover:before {
  opacity: 0.12;
  background-color: rgb(76, 178, 241);
}
.v-list .v-list-item--active.sidebar__item--active,
.v-list .v-list-item--active.sidebar__item--active .v-icon {
  color: #0093ee !important;
}

.scroll-top-fab {
  margin-right: 25px;
  margin-bottom: 35px;
}
</style>